import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    EventEmitter,
    inject,
    Input,
    Output,
    TemplateRef
} from '@angular/core';
import { OffClickDirective } from '@df/module-off-click/off-click.directive';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { ToggleClassDirective } from '@df/ui/toggle/toggle-class.directive';
import { BehaviorSubject } from 'rxjs';
/**
 * Sample usage:
 *
 *     <dropdown [value]="sort.option"
 *               (valueChange)="sort.by($event)"
 *               [options]="sort.getOptions()">
 *         <ng-template #titleTpl>{{sort.selectionLabel}}</ng-template>
 *         <ng-template #optionTpl let-option>{{option.label}}</ng-template>
 *     </dropdown>
 */
@Component({
    selector: 'dropdown',
    templateUrl: './dropdown.html',
    styleUrl: './dropdown.scss',
    imports: [HideUpComponent, OffClickDirective, ToggleClassDirective, NgTemplateOutlet],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DropdownComponent<T> {
    /**
     * No selection placeholder
     */
    @Input()
    placeholder?: string;

    /**
     * Selected value
     */
    @Input()
    value?: T;

    /**
     * Value output
     */
    @Output()
    valueChange = new EventEmitter<T>();

    /**
     * Options to print
     */
    @Input()
    options?: T[];

    @Output()
    openChange = new EventEmitter<boolean>();

    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    /**
     * If is open
     */
    readonly openState = new BehaviorSubject(false);

    /**
     * Title presentation teplate
     */
    @ContentChild('titleTpl', { static: false })
    titleTpl?: TemplateRef<any>;

    /**
     * Option presentation template
     */
    @ContentChild('optionTpl', { static: false })
    optionTpl?: TemplateRef<any>;

    /**
     * Toggle state
     */
    toggle(to = !this.openState.value) {
        this.openState.next(to);
        this.openChange.emit(this.openState.value);
        this.detectChanges();
    }

    pick(option: T) {
        this.toggle(false);
        this.valueChange.emit((this.value = option));
    }

    private detectChanges() {
        this.changeDetectorRef.detectChanges();
    }
}
